import { Fragment, type FC } from 'react';
import { isNil } from 'lodash';

import { LoadingPage } from '#packages/web/layouts/page/templates';
import type { WithViewModelProps } from '#packages/web/types/view-model-props';

import { useAuth } from '#apps/client-web/app/hooks/use-auth';
import { ThrowError } from '#apps/client-web/app/components/throw-error';
import { MainPageLayout } from '#apps/client-web/app/components/main-page-layout';

import { GuestUserProfilePageContent } from '../../components/guest-user-profile-page-content';
import { SignedInUserProfilePageContent } from '../../components/signed-in-user-profile-page-content';

export type UserProfilePageProps = WithViewModelProps<{
  auth: ReturnType<typeof useAuth>;
}>;

export const UserProfilePage: FC<UserProfilePageProps> = ({
  viewModel: { auth },
}: UserProfilePageProps) => {
  const showLoading = auth.isLoading;
  const showError = auth.isError && !showLoading;
  const showContent = !showLoading && !showError;

  const showSignedInProfile = showContent && !isNil(auth.profile);
  const showGuestProfile = showContent && isNil(auth.profile);

  return (
    <Fragment>
      {/* Show loading page */}
      {showLoading && <LoadingPage />}

      {/* Throw error to be caught by error boundary */}
      {showError && <ThrowError error={auth.error!} />}

      {/* Show signed in user profile card */}
      {showSignedInProfile && (
        <SignedInUserProfilePageContent
          onSignOutClick={auth.signOut}
          userProfile={auth.profile!}
        />
      )}

      {/* Show guest user profile card */}
      {showGuestProfile && (
        <GuestUserProfilePageContent onSignInClick={auth.signIn} />
      )}
    </Fragment>
  );
};

export const withViewModel = () => (
  <MainPageLayout title="Profile" pageContentProps={{ maxWidth: 'sm' }}>
    <UserProfilePage
      viewModel={{
        auth: useAuth(),
      }}
    />
  </MainPageLayout>
);

export default withViewModel;
